import { Search, X } from 'lucide-react';
import { FilterDropdown } from '../../../../../components';

const DROPDOWN_CLASS =
  'h-9 min-w-[7.5rem] max-w-[9.5rem] px-2.5 text-sm text-left flex items-center justify-between border border-[#E4E4E7] rounded-lg bg-white text-[#344054]';

function ToolbarLabel({ children }: { children: string }) {
  return (
    <span className="text-xs font-medium text-[#71717A] whitespace-nowrap shrink-0">
      {children}
    </span>
  );
}

type LlmListToolbarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  providerFilter: string;
  onProviderFilterChange: (value: string) => void;
  providerOptions: { value: string; label: string }[];
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  statusOptions: { value: string; label: string }[];
  sortPreset: string;
  onSortPresetChange: (value: string) => void;
  sortOptions: { value: string; label: string }[];
  sortClearValue: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  pageCount: number;
  totalCount: number;
};

function LlmListToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  providerFilter,
  onProviderFilterChange,
  providerOptions,
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  sortPreset,
  onSortPresetChange,
  sortOptions,
  sortClearValue,
  hasActiveFilters,
  onClearFilters,
  pageCount,
  totalCount,
}: LlmListToolbarProps) {
  const clearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className="mb-4 flex flex-nowrap items-center justify-between gap-3 overflow-x-auto pb-1 w-full min-w-0">
      <div className="flex flex-nowrap items-center gap-3 shrink-0 min-w-0">
        <div className="relative w-56 shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]" />
          <input
            type="search"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full h-9 pl-8 pr-8 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#344054] placeholder:text-[#71717A] focus:outline-none focus:ring-2 focus:ring-[#0A8BC7]/30 focus:border-[#0A8BC7]"
          />
          {searchValue ? (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#344054]"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>

        <div className="flex flex-nowrap items-center gap-2 shrink-0">
          <ToolbarLabel>Filter by</ToolbarLabel>
          <FilterDropdown
            value={providerFilter}
            onChange={onProviderFilterChange}
            options={providerOptions}
            placeholder="Provider"
            clearValue="all"
            showClear={false}
            className={DROPDOWN_CLASS}
          />
          <FilterDropdown
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={statusOptions}
            placeholder="Status"
            clearValue="all"
            showClear={false}
            className={DROPDOWN_CLASS}
          />
        </div>
      </div>

      <div className="flex flex-nowrap items-center gap-3 shrink-0">
        <div className="flex items-center gap-2 shrink-0">
          <ToolbarLabel>Sort by</ToolbarLabel>
          <FilterDropdown
            value={sortPreset}
            onChange={onSortPresetChange}
            options={sortOptions}
            placeholder="Sort"
            clearValue={sortClearValue}
            showClear={false}
            className={`${DROPDOWN_CLASS} min-w-[8.5rem] max-w-[10.5rem]`}
          />
        </div>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onClearFilters}
            className="h-9 px-2 text-xs font-medium text-[#EF4444] hover:underline shrink-0"
          >
            Clear
          </button>
        ) : null}
        <span className="text-xs text-[#71717A] whitespace-nowrap shrink-0">
          {pageCount} / {totalCount}
        </span>
      </div>
    </div>
  );
}

export default LlmListToolbar;
